import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../components/Input";
import Dropdown from "../../../../components/Dropdown";
import { VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";
import { getStartEndOfMonth } from "../../../../utils/methods";
import { ListFilters } from "../../../../interfaces/reservation.interface";
import useDebounce from "../../../../hooks/useDebounce";

interface FilterProps {
  status: any[];
  allStatus: any[];
  platforms: any[];
  allPlatforms: any[];
  properties: any[];
  allProperties: any[];
  allReservationStatus: any[];
  reservationStatus: any[];
  allSourecs: any[];
  sources: any[];
  allManagers: any[];
  managers: any[];
  dropdownSearchData: any;
  allReviewedByOptions: any[];
  reviewedByOptions: any[];
  onDropdownSearchChange: (e: any, name: string) => void;
  initialFilters: ListFilters;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
  activeStatus: any[];
  allActiveStatus: any[];
}

const Filter: React.FC<FilterProps> = ({
  status, allStatus, platforms, allPlatforms, properties, allProperties, allReservationStatus, reservationStatus,
  allSourecs, sources, allManagers, managers, dropdownSearchData, reviewedByOptions, allReviewedByOptions,
  onDropdownSearchChange, initialFilters, onApply, onReset, activeStatus, allActiveStatus
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  const defaultDates = useMemo(() => getStartEndOfMonth(), []);

  // Calculate initial filtersApplied state
  const isApplied = initialFilters.searchText !== '' ||
    initialFilters.property !== '' ||
    initialFilters.platform !== '' ||
    initialFilters.reservationStatus !== '' ||
    initialFilters.status !== '' ||
    initialFilters.source !== '' ||
    initialFilters.assignedTo !== '' ||
    initialFilters.reviewedBy !== '' ||
    initialFilters.startDate !== defaultDates.startDate ||
    initialFilters.endDate !== defaultDates.endDate ||
    initialFilters.isActive !== '';

  const [filtersApplied, setFiltersApplied] = useState(isApplied);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Effect to handle Real-time application
  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText
    };

    const applied = filtersToApply.searchText !== '' ||
      filtersToApply.property !== '' ||
      filtersToApply.platform !== '' ||
      filtersToApply.reservationStatus !== '' ||
      filtersToApply.status !== '' ||
      filtersToApply.source !== '' ||
      filtersToApply.assignedTo !== '' ||
      filtersToApply.reviewedBy !== '' ||
      filtersToApply.isActive !== '' ||
      filtersToApply.startDate !== defaultDates.startDate ||
      filtersToApply.endDate !== defaultDates.endDate;

    setFiltersApplied(applied);

    onApply(filtersToApply);
  }, [debouncedSearchText, filters.property, filters.platform, filters.reservationStatus, filters.status, filters.source, filters.assignedTo, filters.reviewedBy, filters.isActive, filters.startDate, filters.endDate, defaultDates, onApply]);


  const handleInputChange = useCallback((event: any, name: string) => {
    setFilters((prev: ListFilters) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleReset = () => {
    const defaults: ListFilters = {
      searchText: '',
      property: '',
      platform: '',
      reservationStatus: '',
      status: '',
      source: '',
      assignedTo: '',
      reviewedBy: '',
      isActive: '',
      ...getStartEndOfMonth(),
    };
    setFilters(defaults);
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t("common.filters")}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t("common.clearFilters")}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.startDate}
                name={VALIDATION_FIELD_MAPPING.startDate}
                max={filters.endDate}
                placeholder={t('reservations.list.pageTexts.filters.startDate')}
                value={filters.startDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.endDate}
                name={VALIDATION_FIELD_MAPPING.endDate}
                min={filters.startDate || undefined}
                placeholder={t('reservations.list.pageTexts.filters.endDate')}
                value={filters.endDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-4 col-sm-4">
              <Dropdown
                values={filters.property ? [filters.property] : []}
                options={(properties || []).map((property: any) => ({
                  label: property.name,
                  value: property.id
                }))}
                allOptions={(allProperties || []).map((property: any) => ({
                  label: property.name,
                  value: property.id
                }))}
                id={VALIDATION_FIELD_MAPPING.property}
                name={VALIDATION_FIELD_MAPPING.property}
                searchText={dropdownSearchData.property}
                onSearch={onDropdownSearchChange}
                placeholder={t("reservations.list.pageTexts.filters.selectProperty")}
                label={t("reservations.list.pageTexts.filters.property")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-6">
              <Dropdown
                values={filters.assignedTo ? [filters.assignedTo] : []}
                options={(managers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id
                }))}
                allOptions={(allManagers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id
                }))}
                id={VALIDATION_FIELD_MAPPING.assignedTo}
                name={VALIDATION_FIELD_MAPPING.assignedTo}
                searchText={dropdownSearchData.assignedTo}
                onSearch={onDropdownSearchChange}
                placeholder={t("reservations.list.pageTexts.filters.selectAssignedTo")}
                label={t("reservations.list.pageTexts.filters.assignedTo")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.reviewedBy ? [filters.reviewedBy] : []}
                options={(reviewedByOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allReviewedByOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.reviewedBy}
                name={VALIDATION_FIELD_MAPPING.reviewedBy}
                searchText={dropdownSearchData.reviewedBy}
                placeholder={t('reservations.list.pageTexts.filters.selectReviewedBy')}
                label={t('reservations.list.pageTexts.filters.reviewedBy')}
                onChange={handleInputChange}
                onSearch={onDropdownSearchChange}
              />
            </div>

            <div className="col-md-4 col-sm-4">
              <Dropdown
                values={filters.platform ? [filters.platform] : []}
                options={(platforms || []).map((platform: any) => ({
                  label: platform.name,
                  value: platform.id
                }))}
                allOptions={(allPlatforms || []).map((platform: any) => ({
                  label: platform.name,
                  value: platform.id
                }))}
                id={VALIDATION_FIELD_MAPPING.platform}
                name={VALIDATION_FIELD_MAPPING.platform}
                searchText={dropdownSearchData.platform}
                onSearch={onDropdownSearchChange}
                placeholder={t("reservations.list.pageTexts.filters.selectPlatform")}
                label={t("reservations.list.pageTexts.filters.platform")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.source ? [filters.source] : []}
                options={(sources || []).map((source: any) => ({
                  label: source.name,
                  value: source.id
                }))}
                allOptions={(allSourecs || []).map((source: any) => ({
                  label: source.name,
                  value: source.id
                }))}
                id={VALIDATION_FIELD_MAPPING.source}
                name={VALIDATION_FIELD_MAPPING.source}
                searchText={dropdownSearchData.source}
                onSearch={onDropdownSearchChange}
                placeholder={t("reservations.list.pageTexts.filters.selectSource")}
                label={t("reservations.list.pageTexts.filters.source")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.reservationStatus ? [filters.reservationStatus] : []}
                options={(reservationStatus || []).map((reservationStatus: any) => ({
                  label: reservationStatus.name,
                  value: reservationStatus.id
                }))}
                allOptions={(allReservationStatus || []).map((reservationStatus: any) => ({
                  label: reservationStatus.name,
                  value: reservationStatus.id
                }))}
                id={VALIDATION_FIELD_MAPPING.reservationStatus}
                name={VALIDATION_FIELD_MAPPING.reservationStatus}
                searchText={dropdownSearchData.reservationStatus}
                onSearch={onDropdownSearchChange}
                placeholder={t("reservations.list.pageTexts.filters.selectReservationStatus")}
                label={t("reservations.list.pageTexts.filters.reservationStatus")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.status ? [filters.status] : []}
                options={(status || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.status}
                name={VALIDATION_FIELD_MAPPING.status}
                searchText={dropdownSearchData.status}
                onSearch={onDropdownSearchChange}
                placeholder={t("reservations.list.pageTexts.filters.selectStatus")}
                label={t("reservations.list.pageTexts.filters.status")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.isActive ? [filters.isActive] : []}
                options={(activeStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allActiveStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.isActive}
                name={VALIDATION_FIELD_MAPPING.isActive}
                searchText={dropdownSearchData.isActive}
                onSearch={onDropdownSearchChange}
                disableClearable={false}
                placeholder={t("reservations.list.pageTexts.filters.active.selectStatus")}
                label={t("reservations.list.pageTexts.filters.active.status")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-4 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t("reservations.list.pageTexts.filters.searchPlaceholder")}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filter;